import { useState } from 'react'
import { Box, Collapse, Grid, IconButton, Typography, Chip } from '@mui/material'
import {
  referralPriorAuthorization,
  type ReferralPriorAuthorizationRetrieveResponse,
} from '@fireflyhealth/api'
import { useQuery } from '~/shared/hooks/query'
import { Loader } from '~/shared/ui'
import PriorAuthorizationItem from './PriorAuthorizationItem'
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight'
import { format } from 'date-fns'

// `model` is sourced from the snake_case Steerage payload —
// `effectivePriorAuthorization` in `SteerageView.tsx` — which now
// comes from the generated `referralSteerage.retrieve` client.
interface Props {
  model: { pk: number; updated_at: string | null }
}

const PriorAuthorizationRelatedView = ({ model }: Props) => {
  const [isExpanded, setIsExpanded] = useState(false)

  let collapseChipLabel: string | null = null
  const sentDate = model?.updated_at
  if (sentDate) {
    collapseChipLabel = `Updated on ${format(new Date(sentDate), 'MMM d, yyyy')} at ${format(
      new Date(sentDate),
      'h:mmaaa',
    )}`
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Grid sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
          <IconButton edge="start" onClick={() => setIsExpanded(c => !c)}>
            {isExpanded ? <KeyboardArrowDown /> : <KeyboardArrowRight />}
          </IconButton>
          <Typography sx={{ fontSize: 18, fontWeight: '500' }}>Prior Authorization</Typography>
        </Grid>
        {collapseChipLabel ? <Chip color="success" size="small" label={collapseChipLabel} /> : null}
      </Box>
      <Collapse in={isExpanded}>
        <Box sx={{ mt: 2, mb: 4 }}>
          <PriorAuthorizationItemLazy pk={model.pk} />
        </Box>
      </Collapse>
    </Box>
  )
}

export default PriorAuthorizationRelatedView

const usePriorAuthorization = (pk: number) => {
  return useQuery<ReferralPriorAuthorizationRetrieveResponse>(
    referralPriorAuthorization.retrieveQueryKey({ id: pk }),
    async () => {
      const { data } = await referralPriorAuthorization.retrieve({ id: pk })
      return data
    },
    {},
    {
      error: `Failed to fetch prior authorization ${pk}`,
    },
  )
}

// Helper for fetching the full PriorAuthorization prior to rendering the edit form.
const PriorAuthorizationItemLazy = ({ pk }: { pk: number }) => {
  const { data: priorAuth, isPending: isLoading } = usePriorAuthorization(pk)

  if (isLoading || !priorAuth) {
    return <Loader />
  }

  return <PriorAuthorizationItem priorAuth={priorAuth} />
}
